import { AddProjectParams } from '../../../params/redux/client';
import { addProjectSchema } from '../../../schema/client_schema';
import { createprojectThunk } from '../../../redux/thunks/client';
import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: AddStageModal
 * @paragraph: Modal component for creating or updating a pipeline stage. Uses Formik for form handling and integrates
 * with Redux for asynchronous stage creation and updating.
 * @param: {boolean} open - Whether the modal is open or not.
 * @param: {function} onClose - Function to close the modal.
 */

const AddProjectModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    /**
     * @heading3: Variables
     * @ol:
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * initialValues: {StageParams} -  Initial form values for stage creation or update.
     * loaders/selectedPipeline/selectedStage: An object containing the details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { currentUser } = useSelector((state: RootState) => state.app);
    const { loaders } = useSelector((state: RootState) => state.client);
    const { teams } = useSelector((state: RootState) => state.team);
    // Initial form values for stage creation or update
    const initialValues: AddProjectParams = {
        project_name: '',
        email: '',
        send_email: 0,
        assignees: []
    };

    /**
     * @heading3: Functions
     * @heading4: Handles form submission for creating or updating a stage.
     * @param: {StageParams} values - Form values for stage creation or update.
     */
    const handleSubmit = async (values: AddProjectParams) => {
        try {
            await dispatch(createprojectThunk(values)).unwrap();
            onClose();
        } catch (e) {
            // Catch errors
        }
    };

    return <CustomModal open={open} handleClose={loaders.isCreating ? () => null : onClose} title="Adding a Project">
        <Formik
            initialValues={initialValues}
            validationSchema={addProjectSchema}
            onSubmit={handleSubmit}
        >
            {({ values, handleChange, handleBlur, errors, touched, setFieldValue }) => (
                <Form>
                    <Box display="flex" flexDirection="column" gap={2}>
                        {/* Input for stage name */}
                        <CustomInput
                            value={values.project_name}
                            onChange={handleChange('project_name')}
                            onBlur={handleBlur('project_name')}
                            error={touched.project_name && errors.project_name}
                            fullWidth
                            label="Name the project"
                            placeholder="Type here"
                            variant="outlined"
                            disabled={loaders.isCreating}
                            mandatoryFields={true}
                        />
                        <Box><CustomInput
                            value={values.email}
                            onChange={handleChange('email')}
                            onBlur={handleBlur('email')}
                            error={touched.email && errors.email}
                            fullWidth
                            label="Project Email"
                            placeholder="Type here"
                            variant="outlined"
                            disabled={loaders.isCreating}
                            mandatoryFields={true}
                        />
                            <CustomInput
                                type="checkbox"
                                label="Do you want to send welcome email?"
                                checked={values.send_email === 1}
                                onChange={(e) => setFieldValue('send_email', e.target.checked ? 1 : 0)}
                                disabled={loaders.isCreating}
                            />
                        </Box>
                        {/* Input for days due */}
                        <CustomInput
                            type="autocomplete"
                            label="Add team members"
                            value={values.assignees}
                            onChangeForAutoComplete={(newValue) => setFieldValue('assignees', newValue)}
                            onBlur={() => handleBlur('assignees')}
                            options={teams.filter((item) => item.role !== 'organizationOwner' && item.id !== currentUser?.id).map((member) => ({
                                name: `${member.first_name} ${member.last_name}`,
                                id: member.id
                            }))}
                            placeholder="Select Team Members"
                            disabled={loaders.isCreating}
                            fullWidth
                            error={Boolean(touched.assignees && errors.assignees)}
                        />
                        {/* Action buttons for cancel and submit */}
                        <Box display="flex" gap={2} mt={2}>
                            <Button variant="outlined" color="primary" onClick={onClose} fullWidth disabled={loaders.isCreating}>
                                Cancel
                            </Button>
                            <Button type="submit" variant="contained" color="primary" fullWidth disabled={loaders.isCreating}>
                                {loaders.isCreating ? 'Submitting...' : 'Submit'}
                            </Button>
                        </Box>
                    </Box>
                </Form>
            )}
        </Formik>
    </CustomModal>;
};

export default AddProjectModal;